import { useState } from "react";
import useTranslations from "../../core/i18n/useTranslations";
import useUser from "../../core/user/useUser";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";
import { notifyApiErrors } from "../../core/helpers/helpers";
import { deleteLocation } from "../../core/api/locations/locations";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import {
  EditLocationModel,
  KnownLocationListModel,
} from "../../core/api/locations/types";
import { capitalize } from "lodash";

const LocationDeleteModal = ({
  locations,
  isOpen,
  onDelete,
  onClose,
  identifiersPresent = false,
}: {
  locations: KnownLocationListModel[] | EditLocationModel[];
  identifiersPresent?: boolean;
  isOpen: boolean;
  onDelete: (success: boolean) => void;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const user = useUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const type =
    locations.length > 1 ? t("Locations:type_multi") : t("Locations:type");
  const title = t("Common:delete_modal_title").replace("{type}", type);

  const confirmSingleMessage = () =>
    identifiersPresent
      ? t("Locations:delete_locations_with_oi_confirm")
      : t("Common:delete_modal_confirm");
  const confirmMultiMessage = () =>
    identifiersPresent
      ? t("Locations:delete_locations_with_oi_confirm_batch")
      : t("Common:delete_modal_confirm_batch");
  const confirmMessage =
    locations.length > 1 ? confirmMultiMessage() : confirmSingleMessage();
  const confirmMessageText = confirmMessage
    .replace("{type}", type)
    .replace("{count}", `${locations.length}`);
  const successMessage = capitalize(
    t("Common:delete_success").replace("{type}", type),
  );

  const onSubmit = async () => {
    if (locations.length === 0) {
      return;
    }
    setIsLoading(true);
    let success = true;
    try {
      const promises = locations.map((location) =>
        location.id
          ? deleteLocation(location.id.toString(), user.authenticatedRequest)
          : Promise.resolve(),
      );
      await Promise.all(promises);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
      success = false;
    } finally {
      setIsLoading(false);
    }
    if (success) {
      notify({
        message: successMessage,
      });
    }
    onDelete(success);
  };

  return (
    <PrimeModal
      withFooter
      withHeader
      header={title}
      isOpen={isOpen}
      onClose={onClose}
      loading={isLoading}
      fixedWidth="576px"
      submitBtn={{
        text: title,
        variant: "destructive",
        onClick: onSubmit,
      }}
      cancelBtn={{
        text: t("Common:cancel"),
        variant: "secondary",
        onClick: onClose,
      }}
    >
      <div className="grey-600">
        <p>{confirmMessageText}</p>
      </div>
    </PrimeModal>
  );
};

export default LocationDeleteModal;
